import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Heart, ShieldCheck, Zap, Activity } from 'lucide-react';

import tglogo from '../assets/logo.png';
import leftImage from '../assets/login-img.jpeg';
import LoginForm from '../components/Forms/LoginForm';
import LoadingAnimation from '../components/LoadingSpinner';
import SeveritySnackbar from '../components/Snackbar';
import { login2FAUser, loginUser } from '../services/authService';
import SessionExpiredDialog from '../components/Dialog/SessionExpiredDialog';
import Login2FAForm from '../components/Forms/Login2FAForm';

const LoginScreen: React.FC = () => {
    const navigate = useNavigate();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showECG, setShowECG] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);
    const [login2faScreen, setlogin2faScreen] = useState(false);
    const [otp, setOtp] = useState('');

    const handleLogout = useCallback(() => {
        sessionStorage.clear();
        navigate('/');
    }, [navigate]);

    useEffect(() => {
        const expirationTime = sessionStorage.getItem('authTokenExpiration');
        if (expirationTime) {
            const expirationDate = new Date(parseInt(expirationTime) * 1000);
            if (expirationDate < new Date()) {
                setShowSessionExpiredDialog(true);
            }
        }
    }, []);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(false);
        setLoading(true);
        setShowECG(true);

        try {
            const response = await loginUser(username, password);
            const { token, info, token_expiration_time } = response.data;
            sessionStorage.setItem('responseCode', response.code);
            if (token) {
                setSnackbarMessage('Login successful!');
                setSnackbarSeverity('success');
                sessionStorage.setItem('authToken', token);
                sessionStorage.setItem('userInfo', JSON.stringify(info));
                sessionStorage.setItem('authTokenExpiration', token_expiration_time.toString());
                navigate('/home');
            } else if ((response.code === 200 || response.code === 201)
                && (response.message === 'Otp sent to your registered mobile and email.')) {
                setlogin2faScreen(true);
                setSnackbarMessage(response.message || 'OTP Page');
                setSnackbarSeverity('success');
            }
        } catch (error) {
            setError(true);
            if (axios.isAxiosError(error) && error.response) {
                setSnackbarMessage(error.response.data?.message || 'Login failed.');
            } else {
                setSnackbarMessage((error as Error).message || 'An unexpected error occurred.');
            }
            setSnackbarSeverity('error');
        } finally {
            setLoading(false);
            setShowECG(false);
            setSnackbarOpen(true);
        }
    };

    const handle2FALogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(false);
        setLoading(true);
        setShowECG(true);

        try {
            const response = await login2FAUser(
                username,
                password,
                'provider',
                'login',
                'mobile',
                otp
            );
            const { token, info, token_expiration_time } = response.data;
            sessionStorage.setItem('responseCode', response.code);
            if (token) {
                setSnackbarMessage('2FA Verification successful!');
                setSnackbarSeverity('success');
                sessionStorage.setItem('authToken', token);
                sessionStorage.setItem('userInfo', JSON.stringify(info));
                sessionStorage.setItem('authTokenExpiration', token_expiration_time.toString());
                navigate('/home');
            } else {
                setError(true);
                setSnackbarMessage('Invalid Verification Code.');
                setSnackbarSeverity('error');
            }
        } catch (error) {
            setError(true);
            if (axios.isAxiosError(error) && error.response) {
                setSnackbarMessage(error.response.data?.message || 'Verification failed.');
            } else {
                setSnackbarMessage((error as Error).message || 'An unexpected error occurred.');
            }
            setSnackbarSeverity('error');
        } finally {
            setLoading(false);
            setShowECG(false);
            setSnackbarOpen(true);
        }
    };

    return (
        <div className="flex h-screen w-full overflow-hidden bg-white font-primary">
            {/* Left Side: Form Area */}
            <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full md:w-[480px] lg:w-[550px] h-full flex flex-col p-8 md:p-16 justify-between bg-white relative z-20"
            >
                <div>
                    <div className="mb-16 flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-2xl">
                            <img src={tglogo} alt="Logo" className="h-10 w-auto object-contain" />
                        </div>
                        {/* <span className="text-2xl font-black text-slate-900 tracking-tight">TeleGaruda</span> */}
                    </div>

                    <div className="max-w-md mx-auto w-full">
                        <div className="mb-10">
                            <h2 className="text-4xl font-black text-slate-900 mb-3 tracking-tight">
                                {login2faScreen ? "Verify Access" : "Provider Portal"}
                            </h2>
                            <p className="text-slate-500 font-medium">
                                {login2faScreen 
                                    ? "Please enter the security code sent to you." 
                                    : "Enter your credentials to access the healthcare dashboard."}
                            </p>
                        </div>
                        
                        {!login2faScreen ? (
                            <LoginForm
                                username={username}
                                password={password}
                                showPassword={showPassword}
                                loading={loading}
                                onUsernameChange={(e) => setUsername(e.target.value)}
                                onPasswordChange={(e) => setPassword(e.target.value)}
                                onShowPasswordToggle={() => setShowPassword(!showPassword)}
                                onSubmit={handleLogin}
                                helperText={error ? 'Authentication failed' : ''}
                                onForgotPassword={() => {}}
                            />
                        ) : (
                            <Login2FAForm
                                otp={otp}
                                helperText={error ? 'Invalid verification code' : ''}
                                loading={loading}
                                onOTPChange={(e) => setOtp(e.target.value)}
                                handle2FASubmit={handle2FALogin}
                                cancel2FA={() => setlogin2faScreen(false)}
                            />
                        )}
                    </div>
                </div>

                <footer className="pt-10">
                    <div className="flex flex-col space-y-6">
                        <div className="h-px bg-slate-100 w-full" />
                        <div className="flex items-center justify-between">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">© 2026 A2ZHealth</p>
                            <div className="flex space-x-6">
                                <a href="#" className="text-xs font-bold text-slate-400 hover:text-primary transition-colors uppercase tracking-widest">Privacy</a>
                                <a href="#" className="text-xs font-bold text-slate-400 hover:text-primary transition-colors uppercase tracking-widest">Help</a>
                            </div>
                        </div>
                    </div>
                </footer>
            </motion.div>

            {/* Right Side: Visual Area */}
            <div className="hidden md:block flex-1 relative bg-slate-900 overflow-hidden">
                <motion.div 
                    initial={{ scale: 1.1, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 1.5 }}
                    className="absolute inset-0"
                >
                    <img 
                        src={leftImage} 
                        alt="Medical" 
                        className="w-full h-full object-cover opacity-60 mix-blend-overlay"
                    />
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-transparent to-slate-900/90" />
                </motion.div>

                {/* Floating Content over Image */}
                <div className="absolute inset-0 flex flex-col justify-center p-20 z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="max-w-lg"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-bold uppercase tracking-widest mb-8">
                            <Zap size={14} className="text-yellow-400 fill-yellow-400" />
                            Next-Gen Telemedicine
                        </div>
                        <h1 className="text-6xl font-black text-white leading-[1.1] mb-8 tracking-tighter">
                            Modern Care, <br />
                            <span className="text-primary-light">Connected.</span>
                        </h1>
                        
                        <div className="grid grid-cols-2 gap-6">
                            <div className="p-6 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10">
                                <ShieldCheck className="text-primary-light mb-3" size={28} />
                                <h4 className="text-white font-bold mb-1">Secure Data</h4>
                                <p className="text-white/60 text-sm leading-relaxed">Enterprise-grade security for patient records.</p>
                            </div>
                            <div className="p-6 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10">
                                <Activity className="text-primary-light mb-3" size={28} />
                                <h4 className="text-white font-bold mb-1">Real-time</h4>
                                <p className="text-white/60 text-sm leading-relaxed">Seamless video and health vitals monitoring.</p>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] left-[-10%] w-[300px] h-[300px] bg-primary-dark/30 rounded-full blur-[100px]" />
            </div>

            <LoadingAnimation show={showECG} onComplete={() => setShowECG(false)} />
            <SeveritySnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={() => setSnackbarOpen(false)}
            />

            {showSessionExpiredDialog && (
                <SessionExpiredDialog onLogout={handleLogout} onClose={() => setShowSessionExpiredDialog(false)} />
            )}
        </div>
    );
};

export default LoginScreen;
